import { Controller } from "@hotwired/stimulus"

// Accessible segmented tab control for the "create verifier" page. Each tab
// button reveals one creation-method panel (upload document / quick start /
// write manually) while hiding the others.
//
// Targets:
//   - tab:   each role="tab" <button> (carries data-panel="<key>")
//   - panel: each role="tabpanel" container (carries data-panel="<key>")
//
// Values:
//   - active: the data-panel key to show on connect (defaults to first tab)
//
// Active button styling is driven by the `is-active` class plus aria-selected,
// so the view owns the exact look.
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { active: String }

  connect() {
    const initial =
      this.activeValue ||
      (this.hasTabTarget ? this.tabTargets[0].dataset.panel : null)
    if (initial) this.activate(initial)
  }

  select(event) {
    if (event.currentTarget.disabled) return

    const key = event.currentTarget.dataset.panel
    if (key) this.activate(key)
  }

  // Arrow / Home / End keyboard navigation across the tablist.
  keydown(event) {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"]
    if (!keys.includes(event.key)) return
    event.preventDefault()

    const tabs = this.tabTargets.filter(tab => !tab.disabled)
    const currentIndex = tabs.indexOf(event.currentTarget)
    if (currentIndex === -1 || tabs.length === 0) return

    let nextIndex = currentIndex

    switch (event.key) {
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % tabs.length
        break
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = tabs.length - 1
        break
    }

    const nextTab = tabs[nextIndex]
    this.activate(nextTab.dataset.panel)
    nextTab.focus()
  }

  activate(key) {
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.panel === key
      tab.classList.toggle("is-active", isActive)
      tab.setAttribute("aria-selected", isActive ? "true" : "false")
      // Roving tabindex: only the active tab is in the tab order.
      tab.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.panelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.panel !== key)
    })
  }
}
